import React from "react";

function HoverPreview({
    poster, mp4Src, webmSrc, alt,
}: { poster: string; mp4Src: string; webmSrc?: string; alt: string }) {
    const canHover = typeof window !== 'undefined'
        ? window.matchMedia?.('(hover:hover) and (pointer:fine)')?.matches
        : true;
    const reduceMotion = typeof window !== 'undefined'
        ? window.matchMedia?.('(prefers-reduced-motion: reduce)')?.matches
        : false;

    const ref = React.useRef<HTMLVideoElement | null>(null);

    React.useEffect(() => {
        if (!ref.current || reduceMotion) return;
        const v = ref.current;
        if (canHover) {
            const onEnter = () => { v.preload = 'auto'; v.play().catch(() => { }); };
            const onLeave = () => v.pause();
            v.parentElement?.addEventListener('mouseenter', onEnter);
            v.parentElement?.addEventListener('mouseleave', onLeave);
            v.parentElement?.addEventListener('focusin', onEnter);
            v.parentElement?.addEventListener('focusout', onLeave);
            return () => {
                v.parentElement?.removeEventListener('mouseenter', onEnter);
                v.parentElement?.removeEventListener('mouseleave', onLeave);
                v.parentElement?.removeEventListener('focusin', onEnter);
                v.parentElement?.removeEventListener('focusout', onLeave);
            };
        }
    }, [canHover, reduceMotion]);

    return (
        <div className="relative overflow-hidden rounded-xl">
            <img src={poster} alt={alt} className="w-full block" />
            {!reduceMotion && (
                <video
                    ref={ref}
                    className="absolute inset-0 w-full h-full object-cover opacity-0 pointer-events-none transition-opacity duration-200"
                    muted playsInline loop preload="none" aria-hidden="true" poster={poster}
                    style={canHover ? {} : { display: 'none' }}
                >
                    {webmSrc && <source src={webmSrc} type="video/webm" />}
                    <source src={mp4Src} type="video/mp4" />
                </video>
            )}
            {!canHover && !reduceMotion && (
                <button type="button"
                    className="absolute right-2 bottom-2 border border-br rounded-full px-2 py-1 bg-su"
                    onClick={() => ref.current?.play().catch(() => { })}
                    aria-label="Play preview video"
                >▶</button>
            )}
            <style>{`
        @media (hover:hover) and (pointer:fine) {
          .hover-preview:hover video { opacity: 1; }
          .hover-preview:focus-within video { opacity: 1; }
        }
      `}</style>
        </div>
    );
}

export default HoverPreview;